import React, { useEffect, useState } from "react";
import assetUrl from "../utils/assetUrl";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaBell,
  FaMoon,
  FaSearch,
  FaSun,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import "./Navbar.css";
import NotificationsBell from "./NotificationsBell";
import { clearAuthSession, getStoredUser, isAuthenticated } from "../utils/auth";
import { ADMIN_NAV_ROLES, hasAnyRole } from "../utils/adminRoles";
import { useLanguage } from "../i18n";

const NAV_LINKS = [
  { labelKey: "home", to: "/" },
  { labelKey: "categories", to: "/categories" },
  { labelKey: "advice", to: "/advice" },
  { labelKey: "profiles", to: "/profiles" },
  { labelKey: "messages", to: "/messages" },
  { labelKey: "dashboard", to: "/dashboard" },
  { labelKey: "myListings", to: "/my-listings" },
  { labelKey: "contact", to: "/contact" },
];

const Navbar = ({ goToPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("theme") === "dark"
  );
  const [user, setUser] = useState(() => getStoredUser());

  useEffect(() => {
    const value = darkMode ? "dark" : "light";
    document.body.setAttribute("data-theme", value);
    document.documentElement.setAttribute("data-theme", value);
    localStorage.setItem("theme", value);
  }, [darkMode]);

  useEffect(() => {
    const syncUser = () => setUser(getStoredUser());
    syncUser();
    window.addEventListener("auth-changed", syncUser);
    window.addEventListener("storage", syncUser);

    return () => {
      window.removeEventListener("auth-changed", syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, [location.pathname]);

  const isAdmin = hasAnyRole(user, ADMIN_NAV_ROLES);
  const loggedIn = isAuthenticated();

  const handleLogoClick = () => {
    navigate("/");
    setMenuOpen(false);
    setTimeout(() => goToPage && goToPage("home"), 50);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?query=${encodeURIComponent(search.trim())}`);
    setMenuOpen(false);
  };

  const handleAccountClick = () => {
    navigate(loggedIn ? "/profile" : "/login");
    setMenuOpen(false);
  };

  const handleLogout = () => {
    clearAuthSession();
    setMenuOpen(false);
    navigate("/login");
  };

  return (
    <header className="navbar">
      <div className="navbar__inner">
        <button
          type="button"
          className="navbar__brand"
          onClick={handleLogoClick}
          aria-label="Go to home page"
        >
          <img
            src={assetUrl("/img/logoN.webp")}
            width="128"
            height="128"
            alt="BytaMed logo"
            className="logo"
            decoding="async"
            fetchPriority="high"
          />

          <span className="navbar__brand-copy">
            <strong>BytaMed</strong>
            <small>{t("brandTagline")}</small>
          </span>
        </button>

        <form
          className="navbar__search"
          onSubmit={handleSearch}
          role="search"
          aria-label="Site search"
        >
          <FaSearch className="navbar__search-icon" />
          <input
            aria-label={t("search")}
            type="text"
            placeholder={t("searchPlaceholder")} aria-label={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">{t("search")}</button>
        </form>

        <div className="navbar__actions">
          <button
            type="button"
            className="navbar__icon-btn"
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            onClick={() => setDarkMode((v) => !v)}
          >
            {darkMode ? <FaSun /> : <FaMoon />}
          </button>

          <select
            className="navbar__language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            aria-label={t("language")}
          >
            <option value="en">EN</option>
            <option value="sv">SV</option>
          </select>

          <NotificationsBell />

          <button
            type="button"
            className="navbar__profile"
            onClick={handleAccountClick}
          >
            <FaUserCircle />
            <span>{user?.name || (loggedIn ? t("myProfile") : t("account"))}</span>
          </button>

          {loggedIn && (
            <button
              type="button"
              className="navbar__profile"
              onClick={handleLogout}
            >
              <FaBell style={{ opacity: 0 }} />
              <span>{t("logout")}</span>
            </button>
          )}

          <button
            type="button"
            className="navbar__menu-btn"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      <nav className={`navbar__nav ${menuOpen ? "is-open" : ""}`}>
        {NAV_LINKS.map((item) => (
          <button
            key={item.to}
            type="button"
            className={`navbar__link ${
              location.pathname === item.to ? "is-active" : ""
            }`}
            onClick={() => {
              navigate(item.to);
              setMenuOpen(false);
            }}
          >
            {t(item.labelKey)}
          </button>
        ))}

        {loggedIn && (
          <button
            type="button"
            className={`navbar__link ${
              location.pathname.startsWith("/profile") ? "is-active" : ""
            }`}
            onClick={() => {
              navigate("/profile");
              setMenuOpen(false);
            }}
          >
            {t("myProfile")}
          </button>
        )}

        {isAdmin && (
          <button
            type="button"
            className={`navbar__link ${
              location.pathname === "/admin" ? "is-active" : ""
            }`}
            onClick={() => {
              navigate("/admin");
              setMenuOpen(false);
            }}
          >
            {t("admin")}
          </button>
        )}
      </nav>
    </header>
  );
};

export default Navbar;